import React, { useState, useEffect } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input, Alert, Spinner, UncontrolledTooltip, Card, CardBody, Table, Badge } from "reactstrap";
import { MaterialRequestService } from "../services/MaterialRequestService";
import { SolicitudInventarioRepository } from "../repositories/SolicitudInventarioRepository";
import { ReservaInventarioRepository } from "../repositories/ReservaInventarioRepository";
import { pb } from "../helpers/pocketbase_helper";
import { formatProductoNombre } from "../utils/productoInventarioHelper";

const SolicitarMaterialButton = ({ ordenId, onMaterialRequested, disabled = false }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [materiales, setMateriales] = useState([]);
  const [materialId, setMaterialId] = useState("");
  const [cantidad, setCantidad] = useState("");
  const [inventarioLocal, setInventarioLocal] = useState(true); // Default to local inventory
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [allocationResult, setAllocationResult] = useState(null);
  const [currentReservas, setCurrentReservas] = useState([]);
  const [currentSolicitudes, setCurrentSolicitudes] = useState([]);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [editQuantity, setEditQuantity] = useState("");
  const [inventoryInfo, setInventoryInfo] = useState(null);
  const [comentario, setComentario] = useState("");
  const [viewingComments, setViewingComments] = useState(null);
  const [commentsModalOpen, setCommentsModalOpen] = useState(false);
  const [isOrdenActiva, setIsOrdenActiva] = useState(false);
  const [reservaTotals, setReservaTotals] = useState([]);
  const [solicitudTotals, setSolicitudTotals] = useState([]);

  // Initialize repositories
  const solicitudRepo = new SolicitudInventarioRepository();
  const reservaRepo = new ReservaInventarioRepository();

  /**
   * Calculate net totals for materials, grouping by producto_inventario ID
   *
   * This function aggregates reservations and requests to calculate the net total
   * for each material. Records with devolucion=true are EXCLUDED from the calculation
   * because they are audit records - the original reservation/request is already updated
   * with the correct quantity. Including devoluciones would count the decrease twice.
   *
   * @param {Array} reservas - Array of reservation objects
   * @param {Array} solicitudes - Array of request objects
   * @returns {Array} Array of material totals with materialId, materialName, netTotal, etc.
   *
   * @example
   * const totals = calculateMaterialTotals(reservas, solicitudes);
   * // Returns: [{ materialId: 'mat-001', materialName: 'Material A', netTotal: 50, ... }]
   */
  const calculateMaterialTotals = (reservas, solicitudes) => {
    const materialMap = new Map();

    // Process reservations - SKIP devolucion records (they are audit-only)
    reservas.forEach((res) => {
      const isDevolucion = res.devolucion === true;

      // Skip devolucion records - they are for audit only
      // The original reservation is already updated with the correct quantity
      if (isDevolucion) {
        return;
      }

      const materialId = res.producto_inventario;
      const materialName = res._materialName || 'Sin nombre';
      const quantity = res.total || 0;

      if (!materialMap.has(materialId)) {
        materialMap.set(materialId, {
          materialId,
          materialName,
          netTotal: 0,
          positiveTotal: 0,
          negativeTotal: 0,
          entries: []
        });
      }

      const material = materialMap.get(materialId);
      material.positiveTotal += quantity;
      material.netTotal += quantity;
      material.entries.push({ type: 'reserva', quantity, isDevolucion: false });
    });

    // Process solicitudes (requests) - SKIP devolucion records (they are audit-only)
    solicitudes.forEach((sol) => {
      const isDevolucion = sol.devolucion === true;

      // Skip devolucion records - they are for audit only
      // The original solicitud is already updated with the correct quantity
      if (isDevolucion) {
        return;
      }

      const materialId = sol.producto_inventario;
      const materialName = sol._materialName || 'Sin nombre';
      const quantity = sol.total || 0;

      if (!materialMap.has(materialId)) {
        materialMap.set(materialId, {
          materialId,
          materialName,
          netTotal: 0,
          positiveTotal: 0,
          negativeTotal: 0,
          entries: []
        });
      }

      const material = materialMap.get(materialId);
      material.positiveTotal += quantity;
      material.netTotal += quantity;
      material.entries.push({ type: 'solicitud', quantity, isDevolucion: false });
    });

    return Array.from(materialMap.values());
  };

  // Fetch available materials and current reservations/requests when modal opens
  useEffect(() => {
    if (modalOpen) {
      fetchAllData();
    }
  }, [modalOpen]);

  // Calculate material totals when reservations or requests change
  useEffect(() => {
    if (currentReservas.length > 0 || currentSolicitudes.length > 0) {
      // Calculate separate totals for each table
      const reservasOnly = calculateMaterialTotals(currentReservas, []);
      const solicitudesOnly = calculateMaterialTotals([], currentSolicitudes);
      setReservaTotals(reservasOnly);
      setSolicitudTotals(solicitudesOnly);
    } else {
      setReservaTotals([]);
      setSolicitudTotals([]);
    }
  }, [currentReservas, currentSolicitudes]);

  // Combined function to fetch all data in parallel with optimized expands
  const fetchAllData = async () => {
    setLoading(true);
    setError("");
    try {
      const orden = await pb.collection("orden_produccion").getOne(ordenId);
      setIsOrdenActiva(!!orden?.activa);

      // Cargar todos los productos de inventario activos con sus materiales
      const productosActivos = await pb.collection("producto_inventario").getFullList({
        filter: "activo=true",
        expand: "material",
        sort: "nombre"
      });

      const productosConMaterial = productosActivos
        .map((producto) => {
          return {
            id: producto.id,
            productoInventarioId: producto.id,
            materialId: producto.id, // Use producto_inventario ID as the material ID
            nombre: formatProductoNombre(producto) || "Sin nombre",
            type: "Material",
            total: producto.total || 0,
            reservado: producto.reservado || 0,
            solicitado: producto.solicitado || 0
          };
        })
        .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));

      setMateriales(productosConMaterial);
      if (productosConMaterial.length === 0) {
        setError("No se encontraron productos de inventario activos disponibles.");
      }

      // Fetch reservations and requests in parallel (repositories already use expand)
      const [reservasResult, solicitudesResult] = await Promise.all([
        reservaRepo.getByOrden(ordenId, 1, 1000),
        solicitudRepo.getByOrden(ordenId, 1, 1000)
      ]);
      
      // Enrich reservations with producto_inventario names (already expanded)
      const enrichedReservas = reservasResult.items.map((res) => {
        const inv = res.expand?.producto_inventario;
        let materialName = formatProductoNombre(inv);
        return { ...res, _materialName: materialName, _inv: inv, _type: 'reserva' };
      });
      
      setCurrentReservas(enrichedReservas);

      // Enrich requests with producto_inventario names (already expanded)
      const enrichedSolicitudes = solicitudesResult.items.map((sol) => {
        const inv = sol.expand?.producto_inventario;
        let materialName = formatProductoNombre(inv);
        return { ...sol, _materialName: materialName, _inv: inv, _type: 'solicitud' };
      });
      
      setCurrentSolicitudes(enrichedSolicitudes);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Error al cargar los datos: " + err.message);
      setMateriales([]);
      setCurrentReservas([]);
      setCurrentSolicitudes([]);
    }
    setLoading(false);
  };

  const handleOpen = () => {
    setModalOpen(true);
    setMaterialId("");
    setCantidad("");
    setComentario("");
    setAllocationResult(null);
    setError("");
    setSuccess("");
  };

  const handleClose = () => {
    setModalOpen(false);
    setMaterialId("");
    setCantidad("");
    setComentario("");
    setInventarioLocal(true); // Reset to default
    setAllocationResult(null);
    setError("");
    setSuccess("");
    setEditingMaterial(null);
    setEditQuantity("");
    setInventoryInfo(null);
  };

  // New functions for material editing
  const handleEditMaterial = (material) => {
    setEditingMaterial(material);
    setEditQuantity(material.total.toString());
    setComentario(material.comentario || ""); // Show existing comment when editing
    setError("");
    setSuccess("");
  };

  const handleCancelEdit = () => {
    setEditingMaterial(null);
    setEditQuantity("");
    setError("");
    setSuccess("");
  };

  const handleUpdateMaterial = async () => {
    if (!editingMaterial || !editQuantity) {
      setError("Ingrese una cantidad válida");
      return;
    }

    const newQuantity = parseInt(editQuantity);
    if (isNaN(newQuantity) || newQuantity < 0) {
      setError("La cantidad debe ser un número mayor o igual a 0");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      let result;
      const materialType = editingMaterial._type; // 'reserva' or 'solicitud'

      if (materialType === 'reserva') {
        // Update reservation independently
        result = await MaterialRequestService.updateReservationQuantity(
          editingMaterial.id,
          newQuantity
        );
      } else if (materialType === 'solicitud') {
        // Update request independently
        result = await MaterialRequestService.updateRequestQuantity(
          editingMaterial.id,
          newQuantity
        );
      } else {
        throw new Error("Tipo de material no reconocido. Debe ser 'reserva' o 'solicitud'");
      }

      setSuccess(`Material actualizado: ${result.reservado} reservado, ${result.solicitado} solicitado`);
      
      // Refresh all data
      await fetchAllData();
      
      // Reset edit state
      setEditingMaterial(null);
      setEditQuantity("");

      // Callback to parent
      if (onMaterialRequested) {
        onMaterialRequested(result);
      }
    } catch (err) {
      console.error("Error updating material:", err);
      setError("Error al actualizar material: " + err.message);
    }
    setSaving(false);
  };

  const handleCheckInventory = async (productoInventarioId) => {
    try {
      const info = await MaterialRequestService.checkInventoryAvailability(productoInventarioId, 1);
      setInventoryInfo(info);
    } catch (err) {
      console.error("Error checking inventory:", err);
      setError("Error al verificar disponibilidad: " + err.message);
    }
  };

  const handleViewComments = (item) => {
    setViewingComments(item);
    setCommentsModalOpen(true);
  };

  const handleCloseCommentsModal = () => {
    setCommentsModalOpen(false);
    setViewingComments(null);
  };

  const handleRequest = async () => {
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      if (!materialId || !cantidad) {
        throw new Error("Seleccione producto de inventario y cantidad");
      }

      const cantidadNum = parseInt(cantidad);
      if (isNaN(cantidadNum) || cantidadNum <= 0) {
        throw new Error("La cantidad debe ser un número mayor a 0");
      }

      // Call material request service using the producto_inventario ID directly
      const result = await MaterialRequestService.addMaterialToOrder(
        ordenId,
        materialId, // producto_inventario ID
        cantidadNum,
        inventarioLocal,
        comentario || null // Pass comment if provided
      );

      setAllocationResult(result);
      setSuccess(`Solicitud creada: ${result.reservado} reservado, ${result.solicitado} solicitado`);

      // Update order document
      if (result.reservaId || result.solicitudId) {
        await pb.collection("orden_produccion").update(ordenId, {});
      }

      // Callback to parent
      if (onMaterialRequested) {
        onMaterialRequested(result);
      }

      // Refresh all data to show updated comments
      await fetchAllData();
      
      // Reset form
      setComentario("");
    } catch (err) {
      console.error("Error requesting material:", err);
      setError("Error al solicitar material: " + err.message);
    }
    setSaving(false);
  };

  const buttonId = `solicitar-material-${ordenId}`;

  return (
    <>
      <Button
        id={buttonId}
        color="info"
        size="sm"
        className="px-2 py-1 me-1"
        onClick={handleOpen}
        disabled={disabled || saving}
      >
        <i className="fas fa-cube"></i>
      </Button>
      <UncontrolledTooltip placement="top" target={buttonId}>
        Solicitar Material
      </UncontrolledTooltip>

      <Modal isOpen={modalOpen} toggle={handleClose} centered size="lg">
        <ModalHeader toggle={handleClose}>
          {editingMaterial ? 'Editar Material' : 'Solicitar Material'}
        </ModalHeader>
        <ModalBody>
          {error && <Alert color="danger">{error}</Alert>}
          {success && <Alert color="success">{success}</Alert>}

          {/* Show current reservations and requests */}
          {(currentReservas.length > 0 || currentSolicitudes.length > 0) && (
            <Card className="mb-3 bg-light">
              <CardBody>
                <h6 className="mb-3">
                  <strong>Materiales Actuales para esta Orden:</strong>
                </h6>

                {currentReservas.length > 0 && (
                  <div className="mb-3">
                    <p className="mb-2">
                      <strong className="text-success">✅ Reservados:</strong>
                    </p>
                    <Table size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Material</th>
                          <th className="text-end">Tipo</th>
                          <th className="text-end">Cantidad</th>
                          <th className="text-center">Origen</th>
                          <th>Comentarios</th>
                          <th className="text-end">Total Neto</th>
                          <th className="text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Group reservations by material */}
                        {reservaTotals.filter(mt => {
                          // Only show materials that have active reservations (exclude devolucion records)
                          return currentReservas.some(res => res.producto_inventario === mt.materialId && res.devolucion !== true);
                        }).map((materialTotal) => {
                          // Filter out devolucion records (audit-only)
                          const materialReservas = currentReservas.filter(res =>
                            res.producto_inventario === materialTotal.materialId && res.devolucion !== true
                          );

                          return (
                            <React.Fragment key={materialTotal.materialId}>
                              {/* Material group header row */}
                              <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                                <td colSpan="5">{materialTotal.materialName}</td>
                                <td className="text-end">
                                  {materialTotal.netTotal > 0 ? (
                                    <Badge color="success">+{materialTotal.netTotal}</Badge>
                                  ) : materialTotal.netTotal < 0 ? (
                                    <Badge color="danger">{materialTotal.netTotal}</Badge>
                                  ) : (
                                    <Badge color="secondary">0</Badge>
                                  )}
                                </td>
                                <td></td>
                              </tr>

                              {/* Individual movement rows */}
                              {materialReservas.map((res) => {
                          const reservado = res.total || 0;
                          const isLocal = res.inventario_local !== false; // Default to true if undefined
                          const isDevolucion = res.devolucion === true;
                          
                          return (
                            <tr key={res.id} style={{ backgroundColor: '#ffffff' }}>
                              <td style={{ paddingLeft: '2rem' }}>
                                <span className="text-muted">↳</span>
                              </td>
                              <td className="text-end">
                                {isLocal ? (
                                  <Badge color="info">Local</Badge>
                                ) : (
                                  <Badge color="warning">Externo</Badge>
                                )}
                              </td>
                              <td className="text-end">
                                {editingMaterial && editingMaterial.id === res.id ? (
                                  <Input
                                    type="number"
                                    value={editQuantity}
                                    onChange={(e) => setEditQuantity(e.target.value)}
                                    min="0"
                                    size="sm"
                                    style={{ width: '80px' }}
                                  />
                                ) : (
                                  <Badge color="success">{reservado}</Badge>
                                )}
                              </td>
                              <td className="text-center">
                                {isDevolucion ? (
                                  <Badge color="danger">Devolución</Badge>
                                ) : (
                                  <Badge color="primary">Solicitud</Badge>
                                )}
                              </td>
                              <td>
                                {res.comentario ? (
                                  <span
                                    title={res.comentario}
                                    style={{ cursor: 'help', fontSize: '0.875rem' }}
                                    className="text-muted"
                                  >
                                    {res.comentario.length > 30 ? res.comentario.substring(0, 30) + '...' : res.comentario}
                                  </span>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td className="text-end">
                                {(() => {
                                  // Show individual entry value (devolucion records are filtered out)
                                  if (reservado > 0) {
                                    return <Badge color="success">+{reservado}</Badge>;
                                  } else {
                                    return <Badge color="secondary">0</Badge>;
                                  }
                                })()}
                              </td>
                              <td className="text-center">
                                {editingMaterial && editingMaterial.id === res.id ? (
                                  <div className="d-flex gap-1 justify-content-center">
                                    <Button
                                      color="success"
                                      size="sm"
                                      onClick={handleUpdateMaterial}
                                      disabled={saving}
                                    >
                                      <i className="fas fa-check"></i>
                                    </Button>
                                    <Button
                                      color="secondary"
                                      size="sm"
                                      onClick={handleCancelEdit}
                                      disabled={saving}
                                    >
                                      <i className="fas fa-times"></i>
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="d-flex gap-1 justify-content-center">
                                    {/* Disable edit if reservation is completed */}
                                    {!res.completado ? (
                                      <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => handleEditMaterial(res)}
                                        disabled={saving}
                                        id={`edit-reserva-${res.id}`}
                                      >
                                        <i className="fas fa-edit"></i>
                                      </Button>
                                    ) : (
                                      <Button
                                        color="secondary"
                                        size="sm"
                                        disabled
                                        id={`edit-reserva-${res.id}`}
                                        title="No se puede editar una reserva completada"
                                      >
                                        <i className="fas fa-lock"></i>
                                      </Button>
                                    )}
                                    {res.comentario && (
                                      <>
                                        <Button
                                          color="info"
                                          size="sm"
                                          onClick={() => handleViewComments(res)}
                                          disabled={saving}
                                          id={`view-comments-reserva-${res.id}`}
                                        >
                                          <i className="fas fa-file-alt"></i>
                                        </Button>
                                        <UncontrolledTooltip placement="top" target={`view-comments-reserva-${res.id}`}>
                                          Ver comentarios
                                        </UncontrolledTooltip>
                                      </>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}

                {currentSolicitudes.length > 0 && (
                  <div>
                    <p className="mb-2">
                      <strong className="text-warning">⚠️ Solicitados:</strong>
                    </p>
                    <Table size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Material</th>
                          <th className="text-end">Cantidad</th>
                          <th>Comentarios</th>
                          <th className="text-end">Total Neto</th>
                          <th className="text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Group solicitudes by material */}
                        {solicitudTotals.filter(mt => {
                          // Only show materials that have active solicitudes (exclude devolucion records)
                          return currentSolicitudes.some(sol => sol.producto_inventario === mt.materialId && sol.devolucion !== true);
                        }).map((materialTotal) => {
                          // Filter out devolucion records (audit-only)
                          const materialSolicitudes = currentSolicitudes.filter(sol =>
                            sol.producto_inventario === materialTotal.materialId && sol.devolucion !== true
                          );

                          return (
                            <React.Fragment key={materialTotal.materialId}>
                              {/* Material group header row */}
                              <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                                <td colSpan="3">{materialTotal.materialName}</td>
                                <td className="text-end">
                                  {materialTotal.netTotal > 0 ? (
                                    <Badge color="success">+{materialTotal.netTotal}</Badge>
                                  ) : materialTotal.netTotal < 0 ? (
                                    <Badge color="danger">{materialTotal.netTotal}</Badge>
                                  ) : (
                                    <Badge color="secondary">0</Badge>
                                  )}
                                </td>
                                <td></td>
                              </tr>

                              {/* Individual movement rows */}
                              {materialSolicitudes.map((sol) => {
                                const cantidad = sol.total || 0;

                                return (
                                  <tr key={sol.id} style={{ backgroundColor: '#ffffff' }}>
                                    <td style={{ paddingLeft: '2rem' }}>
                                      <span className="text-muted">↳</span>
                                    </td>
                                    <td className="text-end">
                                      {editingMaterial && editingMaterial.id === sol.id ? (
                                        <Input
                                          type="number"
                                          value={editQuantity}
                                          onChange={(e) => setEditQuantity(e.target.value)}
                                          min="0"
                                          size="sm"
                                          style={{ width: '80px' }}
                                        />
                                      ) : (
                                        <Badge color={sol.completado ? "success" : "warning"}>
                                          {cantidad} unidades ({sol.completado ? "completado" : "pendiente"})
                                        </Badge>
                                      )}
                                    </td>
                                    <td>
                                      {sol.comentario ? (
                                        <span
                                          title={sol.comentario}
                                          style={{ cursor: 'help', fontSize: '0.875rem' }}
                                          className="text-muted"
                                        >
                                          {sol.comentario.length > 30 ? sol.comentario.substring(0, 30) + '...' : sol.comentario}
                                        </span>
                                      ) : (
                                        <span className="text-muted">-</span>
                                      )}
                                    </td>
                                    <td className="text-end">
                                      {(() => {
                                        // Show individual entry value (devolucion records are filtered out)
                                        if (cantidad > 0) {
                                          return <Badge color="success">+{cantidad}</Badge>;
                                        } else {
                                          return <Badge color="secondary">0</Badge>;
                                        }
                                      })()}
                                    </td>
                                    <td className="text-center">
                                      {editingMaterial && editingMaterial.id === sol.id ? (
                                        <div className="d-flex gap-1 justify-content-center">
                                          <Button
                                            color="success"
                                            size="sm"
                                            onClick={handleUpdateMaterial}
                                            disabled={saving}
                                          >
                                            <i className="fas fa-check"></i>
                                          </Button>
                                          <Button
                                            color="secondary"
                                            size="sm"
                                            onClick={handleCancelEdit}
                                            disabled={saving}
                                          >
                                            <i className="fas fa-times"></i>
                                          </Button>
                                        </div>
                                      ) : (
                                        <div className="d-flex gap-1 justify-content-center">
                                          {/* Disable edit if request is completed */}
                                          {!sol.completado ? (
                                            <Button
                                              color="primary"
                                              size="sm"
                                              onClick={() => handleEditMaterial(sol)}
                                              disabled={saving}
                                              id={`edit-solicitud-${sol.id}`}
                                            >
                                              <i className="fas fa-edit"></i>
                                            </Button>
                                          ) : (
                                            <Button
                                              color="secondary"
                                              size="sm"
                                              disabled
                                              id={`edit-solicitud-${sol.id}`}
                                              title="No se puede editar una solicitud completada"
                                            >
                                              <i className="fas fa-lock"></i>
                                            </Button>
                                          )}
                                          {sol.comentario && (
                                            <>
                                              <Button
                                                color="info"
                                                size="sm"
                                                onClick={() => handleViewComments(sol)}
                                                disabled={saving}
                                                id={`view-comments-solicitud-${sol.id}`}
                                              >
                                                <i className="fas fa-file-alt"></i>
                                              </Button>
                                              <UncontrolledTooltip placement="top" target={`view-comments-solicitud-${sol.id}`}>
                                                Ver comentarios
                                              </UncontrolledTooltip>
                                            </>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {allocationResult && (
            <Alert color="info">
              <strong>Resultado de la Solicitud:</strong>
              <ul className="mb-0 mt-2">
                <li>Reservado: <strong>{allocationResult.reservado}</strong> unidades</li>
                <li>Solicitado: <strong>{allocationResult.solicitado}</strong> unidades</li>
              </ul>
            </Alert>
          )}

          {!allocationResult && (
            <>
              <FormGroup>
                <Label for="material">Producto de Inventario *</Label>
                <Input
                  type="select"
                  name="material"
                  id="material"
                  value={materialId}
                  onChange={(e) => setMaterialId(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Seleccione un producto de inventario</option>
                  {materiales.map(producto => (
                    <option key={producto.id} value={producto.id}>
                      {formatProductoNombre(producto)} {producto.type ? `(${producto.type})` : ''}
                    </option>
                  ))}
                </Input>
              </FormGroup>

              {/* Inventory Information */}
              {materialId && (
                <FormGroup>
                  <Label>Información de Inventario</Label>
                  <div className="d-flex gap-2 align-items-center">
                    <Button
                      color="info"
                      size="sm"
                      onClick={() => handleCheckInventory(materialId)}
                      disabled={loading}
                    >
                      <i className="fas fa-info-circle"></i> Verificar Disponibilidad
                    </Button>
                    {inventoryInfo && (
                      <div className="ms-2">
                        <small className="text-muted">
                          Total: {inventoryInfo.total} | 
                          Reservado: {inventoryInfo.reservado} | 
                          Solicitado: {inventoryInfo.solicitado} | 
                          Disponible: <strong className={inventoryInfo.disponible > 0 ? "text-success" : "text-danger"}>
                            {inventoryInfo.disponible}
                          </strong>
                        </small>
                      </div>
                    )}
                  </div>
                </FormGroup>
              )}

              {/* Source Type Selector - Always available */}
              <FormGroup>
                <Label>Tipo de Fuente *</Label>
                <div>
                  <div className="form-check">
                    <Input
                      type="radio"
                      name="inventarioLocal"
                      id="inventarioLocal"
                      checked={inventarioLocal === true}
                      onChange={() => setInventarioLocal(true)}
                      disabled={loading}
                    />
                    <Label for="inventarioLocal" className="form-check-label">
                      Inventario Local
                    </Label>
                  </div>
                  <div className="form-check">
                    <Input
                      type="radio"
                      name="inventarioLocal"
                      id="proveedorExterno"
                      checked={inventarioLocal === false}
                      onChange={() => setInventarioLocal(false)}
                      disabled={loading}
                    />
                    <Label for="proveedorExterno" className="form-check-label">
                      Proveedor Externo
                    </Label>
                  </div>
                </div>
              </FormGroup>

              <FormGroup>
                <Label for="cantidad">Cantidad Necesaria *</Label>
                <Input
                  type="number"
                  name="cantidad"
                  id="cantidad"
                  placeholder="Ingrese la cantidad"
                  value={cantidad}
                  onChange={(e) => setCantidad(e.target.value)}
                  min="1"
                  disabled={loading}
                />
              </FormGroup>

              {!editingMaterial ? (
                <FormGroup>
                  <Label for="comentario">Comentario</Label>
                  <Input
                    type="textarea"
                    name="comentario"
                    id="comentario"
                    placeholder="Ingrese un comentario opcional (ej: motivo de la solicitud, notas especiales, etc.)"
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                    rows="3"
                    disabled={loading}
                  />
                </FormGroup>
              ) : (
                <FormGroup>
                  <Label for="comentario-existente">Comentario Existente</Label>
                  <Input
                    type="textarea"
                    name="comentario-existente"
                    id="comentario-existente"
                    value={comentario || "(Sin comentario)"}
                    rows="3"
                    disabled
                    readOnly
                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                  />
                  <small className="text-muted">El comentario no se puede modificar al editar la cantidad.</small>
                </FormGroup>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleClose} disabled={saving}>
            {success ? "Cerrar" : "Cancelar"}
          </Button>
          {!allocationResult && (
            <Button color="primary" onClick={handleRequest} disabled={saving || !materialId || !cantidad || loading}>
              {saving ? <Spinner size="sm" className="me-2" /> : null}
              {saving ? "Procesando..." : "Solicitar"}
            </Button>
          )}
        </ModalFooter>
      </Modal>

      {/* Comments Modal */}
      <Modal isOpen={commentsModalOpen} toggle={handleCloseCommentsModal} centered>
        <ModalHeader toggle={handleCloseCommentsModal}>
          Comentarios - {viewingComments?._materialName || 'Material'}
        </ModalHeader>
        <ModalBody>
          {viewingComments?.comentario ? (
            <div>
              <p className="mb-2"><strong>Tipo:</strong> {viewingComments._type === 'reserva' ? 'Reserva' : 'Solicitud'}</p>
              <p className="mb-2"><strong>Cantidad:</strong> {viewingComments.total || 0} unidades</p>
              <hr />
              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {viewingComments.comentario}
              </div>
            </div>
          ) : (
            <p className="text-muted">No hay comentarios disponibles.</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCloseCommentsModal}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default SolicitarMaterialButton;